
import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeftIcon, PaperclipIcon, AlertCircleIcon, ClockIcon, XIcon, ImageIcon, UserIcon, LinkIcon, Link2OffIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { slaMatrix } from "@/data/mockData";
import { getBusinessHoursStatus, isBusinessHours } from "@/utils/businessHours";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getApiEndpoint } from "@/lib/api";
import { formatPhoneNumber } from "@/utils/mask";
import { GLOBAL_WHATSAPP_NUMBER } from "@/utils/whatsapp";
import { useLanguage } from "@/contexts/LanguageContext";
import { getToken, getUser } from "@/lib/auth-storage";

const NewTicket = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    type: "",
    title: "",
    description: "",
    category: "",
    subcategory: "",
    impact: "",
    urgency: "",
    priority: "",
    email: "",
    contactPhone: "",
    company_id: "",
    attachments: [] as File[],
    custom_fields: {} as Record<string, any>
  });

  const [isForMyself, setIsForMyself] = useState<"myself" | "other">("myself");
  const [userEmail, setUserEmail] = useState("");

  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [estimatedSLA, setEstimatedSLA] = useState("");
  const [customFields, setCustomFields] = useState<any[]>([]);
  const [imagePreviews, setImagePreviews] = useState<Record<number, string>>({});
  const [businessHoursStatus, setBusinessHoursStatus] = useState(getBusinessHoursStatus());
  const [categories, setCategories] = useState<any[]>([]);

  // Admin global (base master) nao tem company_id proprio: precisa escolher em qual
  // empresa o chamado sera aberto, senao o backend recusa com 400.
  const [companies, setCompanies] = useState<any[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [needsCompanySelection, setNeedsCompanySelection] = useState(false);
  
  // Estados para chamados similares
  const [similarTicketsDialog, setSimilarTicketsDialog] = useState(false);
  const [similarTickets, setSimilarTickets] = useState<any[]>([]);
  const [createdTicketId, setCreatedTicketId] = useState<string | null>(null);

  useEffect(() => {
    const user = getUser();
    const token = getToken();
    
    // Verificar se está autenticado
    if (!user || !token) {
      toast({
        title: t.newTicket.restrictedAccess,
        description: t.newTicket.restrictedDesc,
        variant: "destructive",
      });
      navigate("/login");
      return;
    }

    try {
      const userData = JSON.parse(user);
      setCurrentUser(userData);
      setUserEmail(userData.email);
      setFormData(prev => ({ ...prev, email: userData.email }));

      if (isMasterBaseUser(userData)) {
        setNeedsCompanySelection(true);
        fetchCompanies();
      }

      // Verificar se veio do catálogo com serviço pré-selecionado
      const preselectedService = location.state?.preselectedService;
      if (preselectedService) {
        // Mapear tipo do serviço para o formato do formulário
        const typeMap: Record<string, string> = {
          'incident': 'Incidente',
          'request': 'Requisição',
          'problem': 'Problema',
          'change': 'Mudança'
        };

        const formType = typeMap[preselectedService.type] || preselectedService.type;
        
        // Pré-preencher os campos do serviço
        setFormData(prev => ({
          ...prev,
          type: formType,
          category: preselectedService.category || '',
          title: preselectedService.name || '',
          description: preselectedService.description || ''
        }));

        toast({
          title: t.newTicket.preselectedService,
          description: t.newTicket.preselectedDesc(preselectedService.name),
        });
      }
    } catch (error) {
      navigate("/login");
    }

    // Buscar categorias do banco
    fetchCategories();
  }, [navigate, toast, location.state]);

  const isMasterBaseUser = (user: any) => {
    if (!user) return false;
    if (user.is_master) return true;
    if (user.role === "admin" && !user.company_id) return true;
    return typeof user.id === "string" && user.id.startsWith("master:");
  };

  const formatWhatsAppNumber = (rawNumber: string) => {
    const digits = rawNumber.replace(/\D/g, "");
    if (digits.length === 13 && digits.startsWith("55")) {
      const country = digits.slice(0, 2);
      const ddd = digits.slice(2, 4);
      const part1 = digits.slice(4, 9);
      const part2 = digits.slice(9);
      return `+${country} ${ddd} ${part1}-${part2}`;
    }
    return digits ? `+${digits}` : "";
  };

  const masterWhatsAppDisplay = formatWhatsAppNumber(GLOBAL_WHATSAPP_NUMBER);
  const buildLimitMessage = (limit?: number, backendMessage?: string) => {
    const fallback = `Você atingiu o limite de ${limit || 30} chamados por empresa no período de teste.`;
    const base = backendMessage || fallback;
    return `${base} Entre em contato com o administrador do sistema pelo WhatsApp ${masterWhatsAppDisplay}.`;
  };

  const fetchCompanies = async () => {
    setIsLoadingCompanies(true);
    try {
      const token = getToken();
      const response = await fetch(getApiEndpoint("/api/companies"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const activeCompanies = (data.companies || []).filter(
        (company: any) => company.is_active === undefined || company.is_active === null || Boolean(company.is_active)
      );
      setCompanies(activeCompanies);
    } catch (error) {
      console.error("Erro ao buscar empresas:", error);
      setCompanies([]);
      toast({
        title: t.newTicket.companyRequired,
        description: t.newTicket.companyRequiredDesc,
        variant: "destructive",
      });
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = getToken();
      const response = await fetch(getApiEndpoint("/api/categories"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Converter para o formato esperado pelo componente
        const formattedCategories = data.categories.map((cat: any) => ({
          name: cat.name,
          subcategories: cat.subcategories ? (Array.isArray(cat.subcategories) ? cat.subcategories : JSON.parse(cat.subcategories)) : []
        }));
        setCategories(formattedCategories);
      } else {
        // Se falhar, usar categorias padrão
        setCategories([
          { name: "Infraestrutura", subcategories: ["Rede", "Servidores", "E-mail", "Internet", "Telefonia"] },
          { name: "Hardware", subcategories: ["Computadores", "Impressoras", "Monitores", "Periféricos"] },
          { name: "Software", subcategories: ["Sistemas Corporativos", "Office", "Antivírus", "Navegadores"] },
          { name: "Acesso e Permissões", subcategories: ["Sistema de Vendas", "ERP", "CRM", "Recursos Humanos"] },
          { name: "Performance", subcategories: ["Lentidão", "Travamentos", "Erro de Sistema", "ERP"] }
        ]);
      }
    } catch (error) {
      console.error("Erro ao buscar categorias:", error);
      // Usar categorias padrão em caso de erro
      setCategories([
        { name: "Infraestrutura", subcategories: ["Rede", "Servidores", "E-mail", "Internet", "Telefonia"] },
        { name: "Hardware", subcategories: ["Computadores", "Impressoras", "Monitores", "Periféricos"] },
        { name: "Software", subcategories: ["Sistemas Corporativos", "Office", "Antivírus", "Navegadores"] },
        { name: "Acesso e Permissões", subcategories: ["Sistema de Vendas", "ERP", "CRM", "Recursos Humanos"] },
        { name: "Performance", subcategories: ["Lentidão", "Travamentos", "Erro de Sistema", "ERP"] }
      ]);
    }
  };

  // Atualizar status de horário de atendimento a cada minuto
  useEffect(() => {
    const interval = setInterval(() => {
      setBusinessHoursStatus(getBusinessHoursStatus());
    }, 60000); // Atualizar a cada minuto

    return () => clearInterval(interval);
  }, []);

  // Atualizar status de horário de atendimento a cada minuto
  useEffect(() => {
    const interval = setInterval(() => {
      setBusinessHoursStatus(getBusinessHoursStatus());
    }, 60000); // Atualizar a cada minuto

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (formData.category && categories.length > 0) {
      const cat = categories.find(c => c.name === formData.category);
      setSelectedCategory(cat);
      setFormData(prev => ({ ...prev, subcategory: "" }));
      
      // Buscar campos personalizados quando tipo e categoria forem selecionados
      if (formData.type) {
        fetchCustomFields(formData.type, formData.category);
      }
    }
  }, [formData.category, formData.type, categories]);

  const fetchCustomFields = async (type: string, category: string) => {
    try {
      const token = getToken();
      const typeMap: Record<string, string> = {
        "Incidente": "incident",
        "Requisição": "request",
        "Problema": "problem",
        "Mudança": "change"
      };
      
      const backendType = typeMap[type] || type.toLowerCase();
      
      const response = await fetch(
        getApiEndpoint(`/api/tickets/forms/custom-fields?type=${backendType}&category=${encodeURIComponent(category)}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const text = await response.text();
          if (text) {
            try {
              const data = JSON.parse(text);
              setCustomFields(data.fields || []);
            } catch (e) {
              // Se não conseguir fazer parse, usar array vazio
              setCustomFields([]);
            }
          }
        }
      }
    } catch (error) {
      // Silenciosamente falha se não houver campos personalizados
      setCustomFields([]);
    }
  };

  useEffect(() => {
    if (formData.impact && formData.urgency) {
      const priority = calculatePriority(formData.impact, formData.urgency);
      setFormData(prev => ({ ...prev, priority }));
      
      if (formData.type && priority) {
        const sla = getSLA(formData.type, priority);
        setEstimatedSLA(sla);
      }
    }
  }, [formData.impact, formData.urgency, formData.type]);

  useEffect(() => {
    if (isForMyself === "myself") {
      setFormData(prev => ({ ...prev, email: userEmail }));
    } else {
      setFormData(prev => ({ ...prev, email: "" }));
    }
  }, [isForMyself, userEmail]);

  const calculatePriority = (impact: string, urgency: string) => {
    const matrix: { [key: string]: { [key: string]: string } } = {
      "Crítico": {
        "Crítico": "Crítica",
        "Alto": "Crítica",
        "Médio": "Crítica",
        "Baixo": "Crítica"
      },
      "Alto": {
        "Crítico": "Crítica",
        "Alto": "Crítica",
        "Médio": "Alta",
        "Baixo": "Média"
      },
      "Médio": {
        "Crítico": "Crítica",
        "Alto": "Alta",
        "Médio": "Média",
        "Baixo": "Baixa"
      },
      "Baixo": {
        "Crítico": "Crítica",
        "Alto": "Média",
        "Médio": "Baixa",
        "Baixo": "Baixa"
      }
    };
    
    return matrix[impact]?.[urgency] || "";
  };

  const getSLA = (type: string, priority: string) => {
    const typeMap: { [key: string]: keyof typeof slaMatrix.Crítica } = {
      "Incidente": "incident",
      "Requisição": "request",
      "Problema": "problem",
      "Mudança": "change"
    };
    
    const slaKey = typeMap[type];
    const priorityKey = priority as keyof typeof slaMatrix;
    
    return slaMatrix[priorityKey]?.[slaKey] || "";
  };

  const addAttachments = (files: File[]) => {
    if (!files.length) return;
    const startIndex = formData.attachments.length;

    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));

    files.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => ({
            ...prev,
            [startIndex + index]: reader.result as string
          }));
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addAttachments(Array.from(e.target.files));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLFormElement>) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageFiles = items
      .filter((item) => item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((file): file is File => Boolean(file));

    if (imageFiles.length > 0) {
      e.preventDefault();
      addAttachments(imageFiles);
      toast({
        title: t.newTicket.printAdded,
        description: t.newTicket.printAddedCount(imageFiles.length),
      });
    }
  };

  const removeAttachment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
    
    // Remover preview correspondente e ajustar índices
    const newPreviews: Record<number, string> = {};
    Object.keys(imagePreviews).forEach(key => {
      const keyNum = parseInt(key);
      if (keyNum < index) {
        newPreviews[keyNum] = imagePreviews[keyNum];
      } else if (keyNum > index) {
        newPreviews[keyNum - 1] = imagePreviews[keyNum];
      }
    });
    setImagePreviews(newPreviews);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Validação básica - apenas campos essenciais
    if (!formData.type || !formData.title || !formData.description || !formData.category) {
      toast({
        title: t.newTicket.requiredFields,
        description: t.newTicket.requiredFieldsDesc,
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }
    
    // Impacto e urgência obrigatórios
    if (!formData.impact || !formData.urgency) {
        toast({
          title: t.newTicket.requiredFields,
          description: t.newTicket.requiredImpactUrgency,
          variant: "destructive",
        });
      setIsLoading(false);
      return;
    }

    const finalUrgency = formData.urgency;
    const finalImpact = formData.impact;

    if (needsCompanySelection && !formData.company_id) {
      toast({
        title: t.newTicket.companyRequired,
        description: t.newTicket.companyRequiredDesc,
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    if (isForMyself === "other" && !formData.email) {
      toast({
        title: t.newTicket.emailRequired,
        description: t.newTicket.emailRequiredDesc,
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      toast({
        title: t.newTicket.invalidEmail,
        description: t.newTicket.invalidEmailDesc,
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    try {
      const token = getToken();
      
      // Converter valores para o formato do backend
      const typeMap: Record<string, string> = {
        "Incidente": "incident",
        "Requisição": "request",
        "Problema": "problem",
        "Mudança": "change"
      };

      const impactMap: Record<string, string> = {
        "Crítico": "critical",
        "Alto": "high",
        "Médio": "medium",
        "Baixo": "low"
      };

      const urgencyMap: Record<string, string> = {
        "Crítico": "critical",
        "Alto": "high",
        "Médio": "medium",
        "Baixo": "low"
      };

      // Se houver anexos, usar FormData, senão usar JSON
      const hasAttachments = formData.attachments && formData.attachments.length > 0;
      
      let response: Response;
      
      if (hasAttachments) {
        // Enviar como FormData para suportar arquivos
        const formDataToSend = new FormData();
        formDataToSend.append("type", typeMap[formData.type] || formData.type.toLowerCase());
        formDataToSend.append("title", formData.title);
        formDataToSend.append("description", formData.description);
        formDataToSend.append("category", formData.category);
        if (formData.subcategory) {
          formDataToSend.append("subcategory", formData.subcategory);
        }
        formDataToSend.append("impact", impactMap[finalImpact] || finalImpact.toLowerCase());
        formDataToSend.append("urgency", urgencyMap[finalUrgency] || finalUrgency.toLowerCase());
        if (formData.email) {
          formDataToSend.append("contact_email", formData.email);
        }
        if (formData.contactPhone) {
          formDataToSend.append("contact_phone", formData.contactPhone);
        }
        if (formData.company_id) {
          formDataToSend.append("company_id", formData.company_id);
        }
        formDataToSend.append("custom_fields", JSON.stringify(formData.custom_fields));
        
        // Adicionar anexos (apenas imagens por enquanto)
        formData.attachments.forEach((file) => {
          if (file.type.startsWith('image/')) {
            formDataToSend.append('attachments', file);
          }
        });

        response = await fetch(getApiEndpoint("/api/tickets"), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            // Não definir Content-Type - deixar o browser definir com boundary para FormData
          },
          body: formDataToSend,
        });
      } else {
        // Enviar como JSON quando não há anexos
        const ticketData = {
          type: typeMap[formData.type] || formData.type.toLowerCase(),
          title: formData.title,
          description: formData.description,
          category: formData.category,
          subcategory: formData.subcategory || null,
          impact: impactMap[finalImpact] || finalImpact.toLowerCase(),
          urgency: urgencyMap[finalUrgency] || finalUrgency.toLowerCase(),
          contact_email: formData.email,
          contact_phone: formData.contactPhone || null,
          custom_fields: formData.custom_fields,
          ...(formData.company_id ? { company_id: Number(formData.company_id) } : {}),
        };

        response = await fetch(getApiEndpoint("/api/tickets"), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(ticketData),
        });
      }

      // Verificar se há conteúdo antes de fazer parse
      const contentType = response.headers.get("content-type");
      const text = await response.text();
      
      // Verificar se a resposta está OK
      if (!response.ok) {
        // Tentar fazer parse do JSON de erro, se houver
        let errorData;
        if (contentType && contentType.includes("application/json") && text) {
          try {
            errorData = JSON.parse(text);
          } catch (e) {
            // Se não conseguir fazer parse, usar mensagem padrão
          }
        }

        // Tratar erros específicos
        if (response.status === 404) {
          throw new Error("Endpoint da API não encontrado. Verifique se a URL da API está configurada corretamente.");
        } else if (response.status === 403) {
          // Verificar se é erro de limite de chamados
          if (!isMasterBaseUser(currentUser) && (errorData?.requires_license || errorData?.tickets_used >= errorData?.tickets_limit)) {
            throw new Error(`Limite de chamados atingido: ${buildLimitMessage(errorData?.tickets_limit, errorData?.message)}`);
          }
          throw new Error(errorData?.error || "Acesso negado. Verifique suas permissões.");
        } else if (response.status >= 500) {
          throw new Error("Erro no servidor. Tente novamente mais tarde.");
        } else {
          throw new Error(errorData?.error || `Erro ao criar chamado (${response.status})`);
        }
      }

      // Processar resposta de sucesso
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Resposta inválida do servidor. A API não retornou JSON.");
      }

      if (!text || text.trim() === "") {
        throw new Error("Resposta vazia do servidor.");
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error("Erro ao processar resposta do servidor. A resposta não é um JSON válido.");
      }

      if (data) {
        // Se o chamado foi vinculado automaticamente a um pai, informar ao usuário
        if (data.ticket.parent_id) {
          toast({
            title: t.newTicket.ticketLinked,
            description: t.newTicket.ticketLinkedDesc(data.ticket.id),
          });
          navigate(`/chamado/${data.ticket.id}`);
          return;
        }
        
        // Se há chamados similares (mas não foi vinculado automaticamente), mostrar diálogo
        if (data.similar_tickets && data.similar_tickets.length > 0) {
          setCreatedTicketId(data.ticket.id);
          setSimilarTickets(data.similar_tickets);
          setSimilarTicketsDialog(true);
          setIsLoading(false);
          return;
        }
        
        toast({
          title: t.newTicket.ticketCreated,
          description: t.newTicket.ticketCreatedDesc(data.ticket.id),
        });
        
        navigate(`/chamado/${data.ticket.id}`);
      } else {
        // Verificar se é erro de limite de chamados
        if (!isMasterBaseUser(currentUser) && data.requires_license && data.tickets_used >= data.tickets_limit) {
          toast({
            title: "Limite de chamados atingido",
            description: buildLimitMessage(data.tickets_limit, data.message),
            variant: "destructive",
          });
        } else {
          throw new Error(data.error || "Erro ao criar chamado");
        }
      }
    } catch (error: any) {
      console.error("Erro ao criar chamado:", error);
      
      // Verificar se é erro de limite
      if (error.message && error.message.includes('Limite de chamados')) {
        toast({
          title: "Limite de chamados atingido",
          description: error.message,
          variant: "destructive",
        });
      } else if (error.message && error.message.includes('Endpoint da API não encontrado')) {
        toast({
          title: "Erro de configuração",
          description: "A URL da API não está configurada corretamente. Entre em contato com o suporte técnico.",
          variant: "destructive",
        });
      } else if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
        toast({
          title: "Erro de conexão",
          description: "Não foi possível conectar ao servidor. Verifique sua conexão com a internet e tente novamente.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Erro ao criar chamado",
          description: error.message || "Não foi possível criar o chamado. Tente novamente.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "Crítica": return "bg-red-100 text-red-800";
      case "Alta": return "bg-orange-100 text-orange-800";
      case "Média": return "bg-yellow-100 text-yellow-800";
      case "Baixa": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <main className="container mx-auto px-4 sm:px-6 py-8 max-w-7xl">
        <div className="mb-8">
          <Link to="/chamados" className="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 mb-4">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            {t.newTicket.backToTickets}
          </Link>
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.newTicket.title}</h1>
              <p className="text-gray-600">{t.newTicket.subtitle}</p>
            </div>
          </div>
          
          {!businessHoursStatus.isBusinessHours && (
            <Alert className="mb-4 border-orange-200 bg-orange-50">
              <ClockIcon className="h-4 w-4 text-orange-600" />
              <AlertTitle className="text-orange-800">{t.newTicket.outsideHours}</AlertTitle>
              <AlertDescription className="text-orange-700">
                {t.newTicket.outsideHoursDesc(businessHoursStatus.nextBusinessHour.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }))}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-8">
            <Card>
              <CardHeader>
                <CardTitle>{t.newTicket.ticketInfo}</CardTitle>
                <CardDescription>
                  {t.newTicket.ticketInfoDesc}
                </CardDescription>
              </CardHeader>
              <CardContent>
            <form onSubmit={handleSubmit} onPaste={handlePaste} className="space-y-6">
                  {needsCompanySelection && (
                    <div className="space-y-2">
                      <Label htmlFor="company_id">
                        {t.newTicket.targetCompany} <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.company_id || undefined}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, company_id: value }))}
                      >
                        <SelectTrigger id="company_id">
                          <SelectValue placeholder={t.newTicket.selectCompany} />
                        </SelectTrigger>
                        <SelectContent>
                          {isLoadingCompanies ? (
                            <SelectItem value="__loading__" disabled>{t.newTicket.loadingCompanies}</SelectItem>
                          ) : companies.length > 0 ? (
                            companies.map((company) => (
                              <SelectItem key={company.id} value={String(company.id)}>
                                {company.name}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="__empty__" disabled>{t.newTicket.noCompanies}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">{t.newTicket.targetCompanyHint}</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="type">{t.newTicket.whatYouNeed}</Label>
                    <Select value={formData.type || undefined} onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder={t.newTicket.select} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Incidente">{t.newTicket.resolveProblem}</SelectItem>
                        <SelectItem value="Requisição">{t.newTicket.requestSomething}</SelectItem>
                        <SelectItem value="Problema">{t.newTicket.reportRecurring}</SelectItem>
                        <SelectItem value="Mudança">{t.newTicket.makeChange}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="title">{t.newTicket.summary}</Label>
                    <Input
                      id="title"
                      placeholder={t.newTicket.summaryPlaceholder}
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      required
                    />
                    <p className="text-xs text-gray-500">{t.newTicket.summaryHint}</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">{t.newTicket.tellMore}</Label>
                    <Textarea
                      id="description"
                      placeholder={t.newTicket.tellMorePlaceholder}
                      rows={5}
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      required
                    />
                    <p className="text-xs text-gray-500">{t.newTicket.tellMoreHint}</p>
                  </div>

                    <div className="space-y-2">
                    <Label htmlFor="category">{t.newTicket.areaOfProblem}</Label>
                      <Select value={formData.category || undefined} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                        <SelectTrigger>
                        <SelectValue placeholder={t.newTicket.selectArea} />
                        </SelectTrigger>
                        <SelectContent>
                          {categories && categories.length > 0 ? (
                            categories.map((cat) => (
                              <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
                            ))
                          ) : (
                            <SelectItem value="__loading__" disabled>{t.newTicket.loadingCategories}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    <p className="text-xs text-gray-500">{t.newTicket.areaHint}</p>
                    </div>

                  {/* Subcategoria - Opcional e simplificada */}
                  {selectedCategory && selectedCategory.subcategories && selectedCategory.subcategories.length > 0 && (
                    <div className="space-y-2">
                      <Label htmlFor="subcategory">
                        {t.newTicket.subcategoryOptional}
                      </Label>
                      <Select 
                        value={formData.subcategory || undefined} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, subcategory: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t.newTicket.subcategoryPlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedCategory.subcategories.map((sub: string) => (
                            <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="impact">{t.newTicket.impact}</Label>
                    <Select 
                      value={formData.impact || undefined} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, impact: value }))}
                    >
                        <SelectTrigger>
                        <SelectValue placeholder={t.newTicket.selectImpact} />
                        </SelectTrigger>
                        <SelectContent>
                        <SelectItem value="Crítico">{t.newTicket.critical}</SelectItem>
                        <SelectItem value="Alto">{t.newTicket.high}</SelectItem>
                        <SelectItem value="Médio">{t.newTicket.medium}</SelectItem>
                        <SelectItem value="Baixo">{t.newTicket.low}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                  <div className="space-y-2">
                    <Label htmlFor="urgency">{t.newTicket.urgency}</Label>
                    <Select 
                      value={formData.urgency || undefined} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, urgency: value }))}
                    >
                        <SelectTrigger>
                        <SelectValue placeholder={t.newTicket.selectUrgency} />
                        </SelectTrigger>
                        <SelectContent>
                        <SelectItem value="Crítico">{t.newTicket.critical}</SelectItem>
                        <SelectItem value="Alto">{t.newTicket.high}</SelectItem>
                        <SelectItem value="Médio">{t.newTicket.medium}</SelectItem>
                        <SelectItem value="Baixo">{t.newTicket.low}</SelectItem>
                        </SelectContent>
                      </Select>
                  </div>

                  {/* Campos Personalizados */}
                  {customFields.length > 0 && (
                    <div className="space-y-4 border-t pt-4">
                      <div>
                      <h3 className="font-semibold text-gray-900">{t.newTicket.additionalInfo}</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {t.newTicket.additionalInfoHint}
                        </p>
                      </div>
                      {customFields.map((field) => (
                        <div key={field.name} className="space-y-2">
                          <Label htmlFor={field.name} className="text-sm">
                            {field.label}
                            {!field.required && <span className="text-gray-400 ml-1 font-normal">(opcional)</span>}
                          </Label>
                          {field.type === 'select' ? (
                            <Select
                              value={formData.custom_fields[field.name] || undefined}
                              onValueChange={(value) =>
                                setFormData(prev => ({
                                  ...prev,
                                  custom_fields: { ...prev.custom_fields, [field.name]: value }
                                }))
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={field.placeholder || `Selecione...`} />
                              </SelectTrigger>
                              <SelectContent>
                                {field.options?.map((option: string) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : field.type === 'textarea' ? (
                            <Textarea
                              id={field.name}
                              placeholder={field.placeholder}
                              value={formData.custom_fields[field.name] || ''}
                              onChange={(e) =>
                                setFormData(prev => ({
                                  ...prev,
                                  custom_fields: { ...prev.custom_fields, [field.name]: e.target.value }
                                }))
                              }
                              required={field.required}
                              rows={3}
                              className="text-sm"
                            />
                          ) : (
                            <Input
                              id={field.name}
                              type={field.type === 'number' ? 'number' : 'text'}
                              placeholder={field.placeholder}
                              value={formData.custom_fields[field.name] || ''}
                              onChange={(e) =>
                                setFormData(prev => ({
                                  ...prev,
                                  custom_fields: { ...prev.custom_fields, [field.name]: e.target.value }
                                }))
                              }
                              required={field.required}
                              className="text-sm"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="space-y-3">
                    <Label>{t.newTicket.forWho}</Label>
                    <RadioGroup value={isForMyself} onValueChange={(value) => setIsForMyself(value as "myself" | "other")}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="myself" id="myself" />
                        <Label htmlFor="myself" className="font-normal cursor-pointer">
                          {t.newTicket.forMyself}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="other" id="other" />
                        <Label htmlFor="other" className="font-normal cursor-pointer">
                          {t.newTicket.forOther}
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">
                      {t.newTicket.contactEmail}
                      {isForMyself === "myself" && (
                        <span className="text-sm text-gray-500 ml-2">{t.newTicket.yourEmailUsed}</span>
                      )}
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={isForMyself === "myself" ? userEmail : "email@exemplo.com"}
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      disabled={isForMyself === "myself"}
                      required
                    />
                    {isForMyself === "myself" && (
                      <p className="text-sm text-gray-500">
                        {t.newTicket.updatesWillBeSent} <strong>{userEmail}</strong>
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contactPhone">{t.newTicket.contactPhone}</Label>
                      <Input
                        id="contactPhone"
                        type="tel"
                        placeholder="(00) 00000-0000"
                        value={formData.contactPhone || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          contactPhone: formatPhoneNumber(e.target.value),
                        }))
                      }
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="attachments">{t.newTicket.attachments}</Label>
                    <div className="flex items-center justify-center w-full">
                      <label htmlFor="attachments" className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <PaperclipIcon className="w-8 h-8 mb-3 text-gray-400" />
                          <p className="mb-2 text-sm text-gray-500">
                            <span className="font-semibold">{t.newTicket.clickToUpload}</span>
                          </p>
                          <p className="text-xs text-gray-500">{t.newTicket.attachmentsHint}</p>
                        </div>
                        <input
                          id="attachments"
                          type="file"
                          multiple
                          accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                    
                    {formData.attachments.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-sm font-medium">{t.newTicket.attachedFiles}</p>
                        {formData.attachments.map((file, index) => {
                          const isImage = file.type.startsWith('image/');
                          const preview = imagePreviews[index];
                          
                          return (
                            <div key={index} className="space-y-2">
                              <div className="flex items-center justify-between p-2 bg-gray-100 rounded">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  {isImage ? (
                                    <ImageIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
                                  ) : (
                                    <PaperclipIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
                                  )}
                                  <span className="text-sm truncate">{file.name}</span>
                                  <span className="text-xs text-gray-500 flex-shrink-0">
                                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                  </span>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeAttachment(index)}
                                  className="flex-shrink-0"
                                >
                                  {t.newTicket.remove}
                                </Button>
                              </div>
                              
                              {/* Preview da imagem */}
                              {isImage && preview && (
                                <div className="relative inline-block">
                                  <img
                                    src={preview}
                                    alt={`Preview ${file.name}`}
                                    className="max-w-xs max-h-48 h-auto rounded-lg border border-gray-200 object-contain"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(index)}
                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                    aria-label="Remover imagem"
                                  >
                                    <XIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? t.newTicket.creating : t.newTicket.createTicket}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar com informações */}
          <div className="space-y-6 lg:col-span-4">
            {/* Prioridade Calculada */}
            {formData.priority && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <AlertCircleIcon className="h-5 w-5 mr-2" />
                    {t.newTicket.calculatedPriority}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Badge className={getPriorityColor(formData.priority)}>
                    {formData.priority}
                  </Badge>
                  <p className="text-sm text-gray-600 mt-2">
                    {t.newTicket.basedOnImpact(formData.impact, formData.urgency)}
                  </p>
                </CardContent>
              </Card>
            )}

            {estimatedSLA && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <ClockIcon className="h-5 w-5 mr-2" />
                    {t.newTicket.estimatedSLA}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">{estimatedSLA}</div>
                  <p className="text-sm text-gray-600 mt-1">
                    {t.newTicket.firstResponseTime}
                  </p>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>{t.newTicket.tipsTitle}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <p>{t.newTicket.tip1}</p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <p>{t.newTicket.tip2}</p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <p>{t.newTicket.tip3}</p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <p>{t.newTicket.tip4}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Diálogo: Chamados Similares Encontrados */}
      <Dialog open={similarTicketsDialog} onOpenChange={setSimilarTicketsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircleIcon className="h-5 w-5 text-blue-600" />
              {t.newTicket.similarTicketsTitle}
            </DialogTitle>
            <DialogDescription>
              {t.newTicket.similarTicketsDesc}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {similarTickets.map((similar: any) => (
              <Card key={similar.id} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="font-mono">{similar.id}</Badge>
                      <Badge className={
                        similar.status === 'open' ? 'bg-blue-100 text-blue-800' :
                        similar.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }>
                        {similar.status === 'open' ? t.dashboard.statusOpen :
                         similar.status === 'in_progress' ? t.dashboard.statusInProgress :
                         similar.status}
                      </Badge>
                    </div>
                    <p className="font-medium text-gray-900 mb-1">{similar.title}</p>
                    <p className="text-sm text-gray-600">{similar.category}</p>
                    {similar.requester && (
                      <p className="text-xs text-gray-500 mt-1">
                        {t.newTicket.reportedBy} {similar.requester.name || similar.requester.email}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleLinkToSimilar(similar.id)}
                    className="shrink-0"
                  >
                    <LinkIcon className="h-4 w-4 mr-1" />
                    {t.newTicket.similarYesSame}
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setSimilarTicketsDialog(false);
                if (createdTicketId) {
                  toast({
                    title: t.newTicket.ticketCreated,
                    description: t.newTicket.ticketCreatedDesc(createdTicketId),
                  });
                  navigate(`/chamado/${createdTicketId}`);
                }
              }}
              className="w-full sm:w-auto"
            >
              {t.newTicket.createNewAnyway}
            </Button>
            <Button
              onClick={() => {
                setSimilarTicketsDialog(false);
                if (createdTicketId) {
                  navigate(`/chamado/${createdTicketId}`);
                }
              }}
              className="w-full sm:w-auto"
            >
              {t.newTicket.viewMyTicket}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Suporte WhatsApp */}

      <Footer />
    </div>
  );
  
  // Vincular a um chamado similar (apenas técnicos podem fazer a vinculação real)
  async function handleLinkToSimilar(parentId: string) {
    if (!createdTicketId) return;
    
    try {
      const token = getToken();
      const userStr = getUser();
      if (!userStr) return;
      
      const user = JSON.parse(userStr);
      
      // Se for técnico, vincular diretamente
      if (user.role === 'internal' || user.role === 'analyst' || user.role === 'admin') {
        const response = await fetch(getApiEndpoint(`/api/tickets/${createdTicketId}/link`), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ parent_id: parentId }),
        });

        if (response.ok) {
          toast({
            title: t.newTicket.ticketsLinked,
            description: t.newTicket.ticketsLinkedDesc,
          });
          setSimilarTicketsDialog(false);
          navigate(`/chamado/${createdTicketId}`);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || "Erro ao vincular");
        }
      } else {
        // Usuário comum - informar que a equipe vai verificar
        toast({
          title: t.newTicket.thanksForInfo,
          description: t.newTicket.teamWillCheck,
        });
        setSimilarTicketsDialog(false);
        navigate(`/chamado/${createdTicketId}`);
      }
    } catch (error: any) {
      toast({
        title: t.newTicket.ticketCreated,
        description: t.newTicket.ticketCreatedVerify,
      });
      setSimilarTicketsDialog(false);
      if (createdTicketId) {
        navigate(`/chamado/${createdTicketId}`);
      }
    }
  }
};

export default NewTicket;
